export const soulsmithColors = {
  obsidianBlack: "#0a0a0f",
  deepBlack: "#020203",
  bloodRed: "#8b0000",
  darkBloodRed: "#2b0707",
  antiqueGold: "#d4af37",
  emberOrange: "#c46a1b",
  ashGrey: "#7a7a7a",
  boneIvory: "#d8c9a3",
  voidPurple: "#3b1458",
  neuralViolet: "#7a2cff",
  cinderBrown: "#3a2417",
  forgeIron: "#1b1d1f",
  deathBlueWhite: "#cfefff",
  primalWhiteGold: "#fff2b0"
} as const;

export const soulsmithSurfaces = {
  obsidianSlab: {
    fill: soulsmithColors.obsidianBlack,
    edge: soulsmithColors.antiqueGold,
    texture: "cracked_obsidian"
  },
  crackedIron: {
    fill: soulsmithColors.forgeIron,
    edge: soulsmithColors.ashGrey,
    texture: "hammered_cracked_iron"
  },
  burnedLeather: {
    fill: soulsmithColors.cinderBrown,
    edge: soulsmithColors.darkBloodRed,
    texture: "burned_leather"
  },
  ashGlass: {
    fill: "rgba(122, 122, 122, 0.22)",
    edge: soulsmithColors.ashGrey,
    texture: "smoked_glass"
  },
  bloodEnamel: {
    fill: soulsmithColors.bloodRed,
    edge: soulsmithColors.darkBloodRed,
    texture: "blood_enamel"
  },
  bonePlate: {
    fill: soulsmithColors.boneIvory,
    edge: soulsmithColors.ashGrey,
    texture: "dry_bone"
  },
  voidCrystal: {
    fill: soulsmithColors.voidPurple,
    edge: soulsmithColors.neuralViolet,
    texture: "void_crystal"
  },
  forgeStone: {
    fill: "#13100d",
    edge: soulsmithColors.emberOrange,
    texture: "heat_cracked_stone"
  }
} as const;

export const soulsmithBorders = {
  jaggedThin: {
    width: 1,
    color: soulsmithColors.antiqueGold,
    clipPath: "polygon(0 16%, 8% 0, 94% 0, 100% 28%, 96% 100%, 5% 100%, 0 76%)"
  },
  jaggedHeavy: {
    width: 2,
    color: soulsmithColors.antiqueGold,
    clipPath: "polygon(0 10%, 6% 0, 92% 0, 100% 18%, 98% 92%, 90% 100%, 4% 100%, 0 82%)"
  },
  brokenGold: {
    width: 1,
    color: soulsmithColors.antiqueGold,
    accent: soulsmithColors.darkBloodRed
  },
  bloodActive: {
    width: 2,
    color: soulsmithColors.bloodRed,
    glow: "0 0 18px rgba(139, 0, 0, 0.75)"
  },
  ashDisabled: {
    width: 1,
    color: soulsmithColors.ashGrey,
    opacity: 0.55
  },
  primalBlinding: {
    width: 2,
    color: soulsmithColors.primalWhiteGold,
    glow: "0 0 28px rgba(255, 242, 176, 0.85)"
  },
  warningFracture: {
    width: 2,
    color: soulsmithColors.bloodRed,
    accent: soulsmithColors.ashGrey
  }
} as const;

export const soulsmithTypography = {
  gothicHeading: {
    fontFamily: '"Cinzel Decorative", Georgia, serif',
    letterSpacing: "0",
    textTransform: "uppercase"
  },
  readableBody: {
    fontFamily: "Inter, system-ui, sans-serif",
    letterSpacing: "0"
  },
  runeAccent: {
    fontFamily: '"Cinzel Decorative", Georgia, serif',
    letterSpacing: "0"
  },
  smallStatLabel: {
    fontFamily: '"Roboto Mono", ui-monospace, monospace',
    letterSpacing: "0"
  },
  combatNumber: {
    fontFamily: '"Roboto Mono", ui-monospace, monospace',
    letterSpacing: "0"
  }
} as const;

export const soulsmithMotion = {
  emberPulse: { durationMs: 900, reducedMotion: "static_glow" },
  ashDrift: { durationMs: 4800, reducedMotion: "static_specks" },
  forgeIgnition: { durationMs: 700, reducedMotion: "quick_fade" },
  runeCarve: { durationMs: 420, reducedMotion: "instant_outline" },
  crackSpread: { durationMs: 860, reducedMotion: "static_sigil" },
  soulTether: { durationMs: 1200, reducedMotion: "solid_line" },
  deathFracture: { durationMs: 1400, reducedMotion: "fade_panel" },
  echoShimmer: { durationMs: 1600, reducedMotion: "static_opacity" }
} as const;

export const soulsmithStates = {
  idle: { edge: soulsmithColors.antiqueGold, fill: soulsmithColors.obsidianBlack },
  hover: { edge: soulsmithColors.antiqueGold, glow: soulsmithColors.emberOrange },
  focus: { edge: soulsmithColors.bloodRed, glow: soulsmithColors.darkBloodRed },
  active: { edge: soulsmithColors.bloodRed, fill: soulsmithColors.darkBloodRed },
  disabled: { edge: soulsmithColors.ashGrey, fill: soulsmithColors.obsidianBlack },
  selected: { edge: soulsmithColors.antiqueGold, fill: soulsmithColors.darkBloodRed },
  danger: { edge: soulsmithColors.bloodRed, fill: soulsmithColors.deepBlack },
  corrupted: { edge: soulsmithColors.neuralViolet, fill: soulsmithColors.voidPurple },
  legendary: { edge: soulsmithColors.voidPurple, glow: soulsmithColors.neuralViolet },
  mythic: { edge: soulsmithColors.antiqueGold, glow: soulsmithColors.emberOrange },
  primal: { edge: soulsmithColors.primalWhiteGold, glow: soulsmithColors.primalWhiteGold },
  offline: { edge: soulsmithColors.ashGrey, fill: soulsmithColors.forgeIron },
  wardenRejected: { edge: soulsmithColors.bloodRed, fill: soulsmithColors.deepBlack }
} as const;

export type SoulsmithColorName = keyof typeof soulsmithColors;
export type SoulsmithStateName = keyof typeof soulsmithStates;
